import React from 'react';
import styled from 'styled-components';
import Button from '../../elements/Button';
import { prop } from 'styled-tools';
import Loader from '../../Loader';
import { H3, H5, P } from '../../elements/Text';
import { useEffectOnce } from 'react-use';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  max-width: ${({ theme }) => theme.dimensions.readableColumnWidth};

  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: -82px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  margin-top: 32px;
`;

const Callout = styled(H5)`
  padding: 16px;
  margin-top: 0;
  background-color: ${prop('theme.colors.transparentBg')};
  border: 1px solid ${prop('theme.colors.transparentBg')};
  border-radius: 8px;
  margin-left: 32px;
`;

export default function TrackSlackOnboardingStep({
  isInvitingToSlack,
  isRefreshing,
  joinSlack,
  onFinish,
}: {
  isInvitingToSlack: boolean;
  isRefreshing: boolean;
  joinSlack: () => void;
  onFinish: () => void;
}) {
  const [messageIndex, setMessageIndex] = React.useState(0);

  const shuffledMessages = React.useMemo(() => {
    const messages = [
      'Did you know? A pie chart is called "camembert" in French.',
      "Did you know? If you shuffle a pack of cards properly, the exact order of the cards you'll have in your hands has almost certainly never been seen in the history of the universe before.",
      "Did you know? In a group of 23 people, there's a 50% chance two of them have the same birthday. It takes 75 people for the probability to increase to 99%.",
      'Did you know? If you fold a piece of paper in half 103 times it would be the thickness of the observable universe.',
      'Did you know? In the next 2 days the world will produce more data than we did from the beginning of human history up to 2003.',
    ];
    return [...messages].sort(() => Math.random() - 0.5);
  }, []);

  const [loadingMessages] = React.useState(shuffledMessages);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setMessageIndex(
        messageIndex < loadingMessages.length - 1 ? messageIndex + 1 : 0
      );
    }, 8000); // update every 8 seconds
    return () => {
      clearInterval(timer);
    };
  }, [messageIndex, loadingMessages]);

  useEffectOnce(() => {
    joinSlack();
  });

  return (
    <Wrapper>
      {isInvitingToSlack ? (
        <>
          <H3>Almost there!</H3>
          <P>
            Hold tight! We're taking into account your preferences and getting
            everything set up for you. This could take up to 20 seconds...
          </P>
          <Row>
            <div style={{ height: '64px', width: '64px' }}>
              <Loader />
            </div>

            <Callout>{loadingMessages[messageIndex]}</Callout>
          </Row>
        </>
      ) : (
        <>
          <H3>Connect to Slack!</H3>
          <P>
            Everything is now ready!{' '}
            <span role="img" aria-label="hug">
              🎉
            </span>
          </P>
          <P>
            The final step is to{' '}
            <b>
              click the personalized invite link that our assistant enkibot
              emailed you
            </b>{' '}
            to join our Enki workspace on Slack.
          </P>
          <P>
            (If you're already in the{' '}
            <a
              href={'https://enkicamp.slack.com'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Enki workspace
            </a>
            , we upgraded your channels there instead of sending an invite.)
          </P>
          <Button
            label={'Finish & Close'}
            onClick={onFinish}
            type="primary"
            isDisabled={isInvitingToSlack}
            isLoading={isRefreshing}
          />
        </>
      )}
    </Wrapper>
  );
}
